import { TextField, MaterialSymbol, withStyles, FieldData } from 'vopay';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import styles from './ChangePasswordForm.module.scss';
import { CircleButton } from 'UI';

const cn = withStyles(styles);

export type BasicOptions = {
  label: string;
  value: string;
  isVisible?: boolean;
};

type ChangePasswordData = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export function buildChangePasswordRows(): FieldData<ChangePasswordData>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Users.UsersList.ChangePassword' });
  const [presets, setPresets] = useState<Record<string, 'password' | 'text'>>({
    currentPassword: 'password',
    newPassword: 'password',
    confirmPassword: 'password',
  });
  const togglePreset = (field: keyof typeof presets) =>
    setPresets((prev) => ({ ...prev, [field]: prev[field] === 'password' ? 'text' : 'password' }));
  return [
    {
      label: () => t('Label.currentPassword'),
      fieldName: 'currentPassword',
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ getFormikData, ctx }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            preset={presets['currentPassword']}
            variant="outlineShadow"
            placeholder={t('Placeholder.currentPassword')}
            elementRight={
              <div className={cn('elementRightContainer')}>
                {ctx.values.currentPassword && (
                  <CircleButton type="button" icon="close" onClick={() => ctx.setFieldValue('currentPassword', '')} />
                )}
                <button type="button" className={cn('showBtn')} onClick={() => togglePreset('currentPassword')}>
                  <MaterialSymbol
                    icon={presets['currentPassword'] === 'password' ? 'visibility' : 'visibility_off'}
                    size="24px"
                    color="#2d2d2d"
                    fillAmount={1}
                    className={cn('icon')}
                  />
                </button>
              </div>
            }
            {...fieldData}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('Label.newPassword'),
      fieldName: 'newPassword',
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ getFormikData, ctx }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            preset={presets['newPassword']}
            variant="outlineShadow"
            placeholder={t('Placeholder.newPassword')}
            elementRight={
              <div className={cn('elementRightContainer')}>
                {ctx.values.newPassword && (
                  <CircleButton type="button" icon="close" onClick={() => ctx.setFieldValue('newPassword', '')} />
                )}
                <button type="button" className={cn('showBtn')} onClick={() => togglePreset('newPassword')}>
                  <MaterialSymbol
                    icon={presets['newPassword'] === 'password' ? 'visibility' : 'visibility_off'}
                    size="24px"
                    color="#2d2d2d"
                    fillAmount={1}
                    className={cn('icon')}
                  />
                </button>
              </div>
            }
            {...fieldData}
          />
        );
      },
      validate: (ctx) =>
        Yup.string()
          .required()
          .test('new-password-validation', t('ValidationMessage.newPassword'), (value: string | undefined) => {
            return value !== ctx.values.currentPassword;
          }),
    },
    {
      label: () => t('Label.confirmPassword'),
      fieldName: 'confirmPassword',
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ getFormikData, ctx }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            preset={presets['confirmPassword']}
            variant="outlineShadow"
            placeholder={t('Placeholder.confirmPassword')}
            elementRight={
              <div className={cn('elementRightContainer')}>
                {ctx.values.confirmPassword && (
                  <CircleButton type="button" icon="close" onClick={() => ctx.setFieldValue('confirmPassword', '')} />
                )}
                <button type="button" className={cn('showBtn')} onClick={() => togglePreset('confirmPassword')}>
                  <MaterialSymbol
                    icon={presets['confirmPassword'] === 'password' ? 'visibility' : 'visibility_off'}
                    size="24px"
                    color="#2d2d2d"
                    fillAmount={1}
                    className={cn('icon')}
                  />
                </button>
              </div>
            }
            {...fieldData}
          />
        );
      },
      validate: (ctx) =>
        Yup.string()
          .required()
          .test('confirm-password-validation', t('ValidationMessage.confirmPassword'), (value: string | undefined) => {
            return value === ctx.values.newPassword;
          }),
    },
  ];
}
